import { computed, onMounted, reactive, ref, watch } from 'vue'

const todayRange = () => {
  const today = new Date()
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, '0')
  const d = String(today.getDate()).padStart(2, '0')
  const value = `${y}-${m}-${d}`
  return [value, value]
}
import { ElMessage, ElMessageBox } from 'element-plus'
import { request } from '../services/adminApi'

const pageRows = (data) => Array.isArray(data) ? data : data?.records || data?.rows || data?.list || []

export function useAdmin() {
  const token = ref(localStorage.getItem('adminToken') || '')
  const adminName = ref(localStorage.getItem('adminName') || '管理员')
  const loading = ref(false)
  const customers = ref([])
  const customerNameCache = reactive({})
  const customerPagination = reactive({ page: 1, size: 10, total: 0, keyword: '' })
  const apps = ref([])
  const appPagination = reactive({ page: 1, size: 20, total: 0, keyword: '', customerId: null })
  const devices = ref([])
  const groups = ref([])
  /** 广告记录筛选专用分组，避免和设备页 groups 互相覆盖 */
  const recordGroups = ref([])
  const recordPlatforms = ref([])
  const records = ref([])
  const recordsLoading = ref(false)
  const recordPagination = reactive({ page: 1, size: 10, total: 0, totalReward: 0 })
  const riskLogs = ref([])
  const selectedCustomerId = ref(null)
  const summary = reactive({
    totalBalance: 0,
    totalCustomers: 0,
    totalDevices: 0,
    totalApps: 0,
    normalCustomers: 0,
    disabledCustomers: 0,
    activeAdCustomers: 0,
    pausedAdCustomers: 0,
    topByDevices: [],
    topByBalance: []
  })
  
  // 筛选条件状态
  const recordFilters = reactive({
    customerId: null,
    groupId: null,
    adPlatform: null,
    adType: null,
    dateRange: todayRange()
  })
  
  const totalDevices = computed(() => Number(summary.totalDevices || 0))
  const call = (path, options) => request(path, token.value, options)

  function rememberCustomers(list) {
    const rows = list || []
    rows.forEach((c) => {
      if (c?.id != null) customerNameCache[c.id] = c.phone || String(c.id)
    })
  }

  const customerName = (id) => customerNameCache[id] || customers.value.find((item) => item.id === id)?.phone || id
  const groupName = (id) => groups.value.find((item) => item.id === id)?.name || id || '-'

  const uniqueAdTypes = computed(() => ['reward', 'interstitial', 'banner'])
  const uniqueAdPlatforms = computed(() => recordPlatforms.value)
  const recordFilterGroups = computed(() => {
    if (!recordFilters.customerId) return []
    return recordGroups.value.map((g) => ({ id: g.id, name: g.name }))
  })

  async function loadCustomers(options = {}) {
    if (options.page != null) customerPagination.page = options.page
    if (options.size != null) customerPagination.size = options.size
    if (options.keyword != null) customerPagination.keyword = options.keyword
    const params = new URLSearchParams({
      page: String(customerPagination.page),
      size: String(customerPagination.size)
    })
    if (customerPagination.keyword) params.set('keyword', customerPagination.keyword)
    const data = await call(`/api/admin/customer/list?${params}`)
    const rows = data?.records || []
    customers.value = rows
    customerPagination.total = Number(data?.total || 0)
    rememberCustomers(rows)
    return data
  }

  // 风控记录分页与筛选
  const riskPagination = reactive({ page: 1, size: 20, total: 0 })
  const riskFilters = reactive({ customerId: null, deviceId: '' })
  async function loadRiskLogs(page, size) {
    if (page != null) riskPagination.page = page
    if (size != null) riskPagination.size = size
    const params = new URLSearchParams()
    params.set('page', riskPagination.page)
    params.set('size', riskPagination.size)
    if (riskFilters.customerId != null) params.set('customerId', riskFilters.customerId)
    if (riskFilters.deviceId) params.set('deviceId', riskFilters.deviceId)
    const data = await call(`/api/admin/log/risk/list?${params}`)
    const pageData = data?.records || []
    riskLogs.value = Array.isArray(pageData) ? pageData : []
    riskPagination.total = data?.total || 0
  }

  function resolveRecordDateRange() {
    const range = recordFilters.dateRange
    if (range?.[0] && range?.[1]) {
      return { startDate: range[0], endDate: range[1] }
    }
    const [startDate, endDate] = todayRange()
    return { startDate, endDate }
  }

  async function loadRecordPlatforms() {
    const { startDate, endDate } = resolveRecordDateRange()
    const params = new URLSearchParams({ startDate, endDate })
    if (recordFilters.customerId != null) params.set('customerId', recordFilters.customerId)
    const data = await call(`/api/admin/ad/platforms?${params}`)
    recordPlatforms.value = Array.isArray(data) ? data : []
  }

  /** 后端筛选分页加载广告记录 */
  async function loadRecords(options = {}) {
    if (options.page != null) recordPagination.page = options.page
    if (options.size != null) recordPagination.size = options.size
    const { startDate, endDate } = resolveRecordDateRange()
    const params = new URLSearchParams({
      page: String(recordPagination.page),
      size: String(recordPagination.size),
      startDate,
      endDate
    })
    if (recordFilters.customerId != null) params.set('customerId', recordFilters.customerId)
    if (recordFilters.groupId != null) params.set('groupId', recordFilters.groupId)
    if (recordFilters.adPlatform) params.set('adPlatform', recordFilters.adPlatform)
    if (recordFilters.adType) params.set('adType', recordFilters.adType)

    recordsLoading.value = true
    try {
      const data = await call(`/api/admin/ad/record/list?${params}`)
      records.value = pageRows(data)
      recordPagination.total = Number(data?.total || 0)
      recordPagination.totalReward = data?.totalReward ?? 0
      await loadRecordPlatforms()
    } finally {
      recordsLoading.value = false
    }
  }

  async function searchRecords() {
    await loadRecords({ page: 1 })
  }

  async function onRecordPageChange({ page, size }) {
    await loadRecords({ page, size })
  }

  /** 广告记录页选客户时，加载该客户分组供筛选 */
  async function loadGroupsForRecordFilter(customerId) {
    if (!customerId) {
      recordGroups.value = []
      return
    }
    const groupData = await call(`/api/admin/device/group/list?customerId=${customerId}`)
    recordGroups.value = groupData || []
  }

  async function loadCustomerDetails() {
    if (!selectedCustomerId.value) {
      devices.value = []
      groups.value = []
      return
    }
    const [deviceData, groupData] = await Promise.all([
      call(`/api/admin/device/list?customerId=${selectedCustomerId.value}`),
      call(`/api/admin/device/group/list?customerId=${selectedCustomerId.value}`)
    ])
    devices.value = deviceData || []
    groups.value = groupData || []
  }
  async function selectCustomer(id) {
    selectedCustomerId.value = id
    await loadCustomerDetails()
  }
  async function loadAll(showMessage = false) {
    loading.value = true
    try {
      const stats = await call('/api/admin/statistics')
      summary.totalBalance = stats?.totalBalance || 0
      summary.totalCustomers = Number(stats?.totalCustomers || 0)
      summary.totalDevices = Number(stats?.totalDevices || 0)
      summary.totalApps = Number(stats?.totalApps || 0)
      summary.normalCustomers = Number(stats?.normalCustomers || 0)
      summary.disabledCustomers = Number(stats?.disabledCustomers || 0)
      summary.activeAdCustomers = Number(stats?.activeAdCustomers || 0)
      summary.pausedAdCustomers = Number(stats?.pausedAdCustomers || 0)
      summary.topByDevices = Array.isArray(stats?.topByDevices) ? stats.topByDevices : []
      summary.topByBalance = Array.isArray(stats?.topByBalance) ? stats.topByBalance : []
      rememberCustomers([...(summary.topByDevices || []), ...(summary.topByBalance || [])])
      riskPagination.page = 1
      riskPagination.size = 20
      riskFilters.customerId = null
      riskFilters.deviceId = ''
      await Promise.all([
        loadCustomers({ page: 1, size: 10, keyword: '' }),
        loadApps({ page: 1, size: 20, keyword: '', customerId: null }),
        loadRiskLogs(1),
        loadRecords({ page: 1 }),
        loadCustomerDetails(),
        loadRiskConfigOptions()
      ])
      if (recordFilters.customerId) {
        await loadGroupsForRecordFilter(recordFilters.customerId)
      }
      if (showMessage) ElMessage.success('数据已刷新')
    } catch (error) {
      ElMessage.error(error.message)
    } finally {
      loading.value = false
    }
  }

  async function loadApps(options = {}) {
    if (options.page != null) appPagination.page = options.page
    if (options.size != null) appPagination.size = options.size
    if (options.keyword != null) appPagination.keyword = options.keyword
    if (options.customerId !== undefined) appPagination.customerId = options.customerId
    const params = new URLSearchParams({
      page: String(appPagination.page),
      size: String(appPagination.size)
    })
    if (appPagination.customerId != null) params.set('customerId', appPagination.customerId)
    if (appPagination.keyword) params.set('keyword', appPagination.keyword)
    const data = await call(`/api/admin/app/list?${params}`)
    apps.value = data?.records || []
    appPagination.total = Number(data?.total || 0)
    rememberCustomers(
      (apps.value || [])
        .filter((a) => a.customerId != null && a.customerPhone)
        .map((a) => ({ id: a.customerId, phone: a.customerPhone }))
    )
  }

  async function login(form) {
    loading.value = true
    try {
      const data = await request('/api/admin/auth/login', '', { method: 'POST', body: JSON.stringify(form) })
      token.value = data.token
      adminName.value = data.realName || data.username
      localStorage.setItem('adminToken', token.value)
      localStorage.setItem('adminName', adminName.value)
      await loadAll()
    } catch (error) {
      ElMessage.error(error.message)
    } finally {
      loading.value = false
    }
  }
  async function createCustomer(form) {
    await call('/api/admin/customer/create', { method: 'POST', body: JSON.stringify({
      phone: form.phone,
      password: form.password,
      balance: Number(form.balance || 0)
    }) })
    ElMessage.success('客户创建成功')
    await loadAll()
  }

  const riskConfigOptions = ref([])
  async function loadRiskConfigOptions() {
    try {
      const data = await call('/api/admin/risk-config/options')
      riskConfigOptions.value = Array.isArray(data) ? data : []
    } catch {
      riskConfigOptions.value = []
    }
  }
  async function createApp(form) {
    await call('/api/admin/app/create', { method: 'POST', body: JSON.stringify({ ...form, status: 1 }) })
    ElMessage.success('应用创建成功')
    await loadApps({ page: 1 })
  }
  async function updateApp(form) {
    await call('/api/admin/app/update', { method: 'PUT', body: JSON.stringify(form) })
    ElMessage.success('应用已更新')
    await loadApps()
  }
  async function toggleAd(item) {
    const enabled = item.adEnabled === 1 ? 0 : 1
    await call(`/api/admin/customer/${item.id}/toggle-ad?enabled=${enabled}`, { method: 'PUT' })
    ElMessage.success(enabled ? '广告已启用' : '广告已暂停')
    await loadAll()
  }
  async function changeStatus(item) {
    const status = item.status === 1 ? 0 : 1
    try {
      await ElMessageBox.confirm(`确认${status ? '恢复' : '禁用'}客户 ${item.phone}？`, '状态变更', { type: 'warning' })
    } catch {
      return // 用户取消
    }
    await call(`/api/admin/customer/${item.id}/status?status=${status}`, { method: 'PUT' })
    await loadAll()
  }
  async function recharge(item) {
    let value
    try {
      const res = await ElMessageBox.prompt('请输入充值次数（正整数）', `为 ${item.phone} 充值额度`, {
        inputValue: '100', inputPattern: /^[1-9]\d*$/, inputErrorMessage: '请输入正整数次数'
      })
      value = res.value
    } catch {
      return
    }
    await call(`/api/admin/customer/${item.id}/recharge?amount=${Number(value)}`, { method: 'POST' })
    ElMessage.success('充值成功')
    await loadAll()
  }
  async function manualAdjust(item) {
    let value
    let remark = ''
    try {
      const res = await ElMessageBox.prompt('请输入调整次数（正加负减，如 -10）', `调账 ${item.phone}`, {
        inputValue: '0', inputPattern: /^-?[1-9]\d*$/, inputErrorMessage: '请输入非 0 整数'
      })
      value = res.value
      const remarkRes = await ElMessageBox.prompt('请输入调账备注（可空）', '调账备注', {
        inputValue: '手动调整', inputPlaceholder: '备注'
      }).catch(() => ({ value: '手动调整' }))
      remark = remarkRes.value || '手动调整'
    } catch {
      return
    }
    await call(
      `/api/admin/customer/${item.id}/manual-adjust?amount=${Number(value)}&remark=${encodeURIComponent(remark)}`,
      { method: 'POST' }
    )
    ElMessage.success('调账成功')
    await loadAll()
  }
  async function refund(item) {
    let value
    let remark = '退款'
    try {
      const res = await ElMessageBox.prompt('请输入退款次数（正整数）', `退款 ${item.phone}`, {
        inputValue: '10', inputPattern: /^[1-9]\d*$/, inputErrorMessage: '请输入正整数'
      })
      value = res.value
      const remarkRes = await ElMessageBox.prompt('请输入退款原因（可空）', '退款备注', {
        inputValue: '退款', inputPlaceholder: '备注'
      }).catch(() => ({ value: '退款' }))
      remark = remarkRes.value || '退款'
    } catch {
      return
    }
    await call(
      `/api/admin/customer/${item.id}/refund?amount=${Number(value)}&remark=${encodeURIComponent(remark)}`,
      { method: 'POST' }
    )
    ElMessage.success('退款成功')
    await loadAll()
  }
  function logout() {
    localStorage.removeItem('adminToken')
    localStorage.removeItem('adminName')
    token.value = ''
  }
  async function clearRecordFilters() {
    recordFilters.customerId = null
    recordFilters.groupId = null
    recordFilters.adPlatform = null
    recordFilters.adType = null
    recordFilters.dateRange = todayRange()
    recordGroups.value = []
    try {
      await loadRecords({ page: 1 })
    } catch (error) {
      ElMessage.error(error.message)
    }
  }

  // 选客户时拉分组并重新查询；改时间后重新查平台与列表
  watch(
    () => recordFilters.customerId,
    async (customerId, prev) => {
      if (customerId === prev) return
      recordFilters.groupId = null
      try {
        await loadGroupsForRecordFilter(customerId)
      } catch (error) {
        ElMessage.error(error.message)
      }
    }
  )
  
  onMounted(() => token.value && loadAll())
  return { token, adminName, loading, customers, customerPagination, customerNameCache, apps, appPagination,
    devices, groups, records, recordsLoading, riskLogs, selectedCustomerId,
    summary, totalDevices, recordFilters, recordPagination, uniqueAdPlatforms, uniqueAdTypes, recordFilterGroups,
    clearRecordFilters, loadRecords, searchRecords, onRecordPageChange, loadCustomers, loadApps,
    riskPagination, riskFilters, loadRiskLogs, riskConfigOptions, loadRiskConfigOptions,
    call, login, logout, loadAll, loadCustomerDetails, selectCustomer, createCustomer,
    createApp, updateApp,
    toggleAd, changeStatus, recharge, manualAdjust, refund, customerName, groupName, rememberCustomers }
}
